import React from 'react';
import {MinusCircle} from 'lucide-react';
import {renderComponent} from '../../dyanimcFormUtil';
import {FormComponent} from "../../DynamicFormTypes.ts";
import {getColSpan} from "../sharedStyles.tsx";
import {useModifiedComponents} from "./datagridUtil.tsx";

// Define props interface for the DataGridRow component
interface DataGridRowProps {
    field: Record<string, any>;
    index: number;
    components: Array<FormComponent>;
    fieldKey: string;
    minLength?: number;
    totalCount: number;
    handleRemoveRow: (index: number) => void;
    itemName: string;
}

const DataGridRowRenderer: React.FC<DataGridRowProps> = (({
                                                             field,
                                                             index,
                                                             components,
                                                             fieldKey,
                                                             minLength,
                                                             totalCount,
                                                             handleRemoveRow,
    itemName
                                                         }) => {

    const modifiedComponents = useModifiedComponents(components, fieldKey, index, field);

    // Render components for column layout
    const renderComponents = () => {
        return modifiedComponents.map((modifiedComponent, compIndex) => {
            return (
                <div
                    key={`${field.id}-comp-${compIndex}`}
                    className={`${getColSpan(modifiedComponent.width || 1)}`}
                >
                    {renderComponent({
                        component: modifiedComponent,
                    })}
                </div>
            );
        });
    };

    // Row Layout: Original horizontal layout
    return (
        <div className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-100'} rounded-lg shadow-sm`}>
            {/* Row header with item number and remove button */}
            <div className="bg-gray-200 px-4 py-2 flex justify-between items-center">
                <div className="text-sm font-medium text-gray-700">
                    {itemName} {index + 1}
                </div>
                <button
                    type="button"
                    onClick={() => handleRemoveRow(index)}
                    disabled={minLength !== undefined && totalCount <= minLength}
                    className={`
                        p-1.5 rounded-full text-red-500 hover:bg-red-50 transition-colors
                        focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-50
                        ${minLength !== undefined && totalCount <= minLength ? 'opacity-40 cursor-not-allowed' : ''}
                    `}
                    aria-label="Remove row"
                >
                    <MinusCircle className="h-5 w-5" />
                </button>
            </div>

            {/* Use CSS Grid for automatic layout */}
            <div className="grid grid-cols-12 gap-3 p-3">
                {renderComponents()}
            </div>
        </div>
    );
})

export const DataGridRow = React.memo(DataGridRowRenderer);
